import { useState } from "react";
import { api, errorText } from "../../lib/api.ts";
import { Turnstile } from "../../lib/Turnstile.tsx";

/** The one sign-in flow: email, then a 6-digit code. Used for sign-up and sign-in. */
export function EmailCode({ mode, onDone }: { mode: "signup" | "signin"; onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [turnstile, setTurnstile] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function start(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ challenge: string }>(`/api/auth/${mode}`, { json: { email, turnstile } });
      setChallenge(r.challenge);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/verify", { json: { challenge, code } });
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!challenge) {
    return (
      <form onSubmit={start} className="flex max-w-md flex-col gap-3">
        <label htmlFor="email" className="text-[15px] font-medium">Email address</label>
        <input id="email" type="email" required autoComplete="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.ac.ug" />
        <Turnstile onToken={setTurnstile} />
        {error && <p role="alert" className="text-[15px] text-bad">{error}</p>}
        <button className="btn btn-primary self-start" disabled={busy}>{busy ? "Sending…" : "Send me a code"}</button>
        <p className="text-[13px] text-ink-3">Temporary inboxes and test addresses can't be used. We send one email with a code; no newsletter.</p>
      </form>
    );
  }
  return (
    <form onSubmit={verify} className="flex max-w-md flex-col gap-3">
      <p className="text-[15px] text-ink-2">We sent a 6-digit code to <b>{email}</b>. It works for 15 minutes.</p>
      <label htmlFor="code" className="text-[15px] font-medium">Code</label>
      <input id="code" inputMode="numeric" pattern="\d{6}" maxLength={6} required autoComplete="one-time-code" className="input font-mono tracking-[0.4em]" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
      {error && <p role="alert" className="text-[15px] text-bad">{error}</p>}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Verify"}</button>
        <button type="button" className="btn" onClick={() => { setChallenge(null); setCode(""); }}>Use a different address</button>
      </div>
    </form>
  );
}
